import React, { useCallback, useEffect, useState } from 'react';
import { shippingApi } from '../../api/shippingApi';
import { formatShareDate } from './supplierShareColumnDefs';
import { ShipRequestPreviewModal } from './ShipRequestPreviewModal';

function arrangeLabel(status) {
  if (status === 'arranged') return '手配済';
  return '未手配';
}

/**
 * 依頼一覧パネル（社内・仕入先共通）。
 */
export function ShipRequestListPanel({
  mode = 'view', // view=社内, edit=仕入先
  onChanged,
}) {
  const isStaff = mode === 'view';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [latestOnly, setLatestOnly] = useState(true);
  const [arrangeFilter, setArrangeFilter] = useState(''); // '' | pending | arranged
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [previewNo, setPreviewNo] = useState(null);
  const [busyNo, setBusyNo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await shippingApi.getShipRequests({
        latestOnly,
        arrangeStatus: arrangeFilter || undefined,
        includeCancelled,
        limit: 200,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setItems([]);
      setError(e?.response?.data?.detail || e?.message || '一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [latestOnly, arrangeFilter, includeCancelled]);

  useEffect(() => {
    load();
  }, [load]);

  const notify = () => {
    if (typeof onChanged === 'function') onChanged();
  };

  const handleReissue = async (requestNo) => {
    if (!window.confirm(`依頼No ${requestNo} を再発行しますか？`)) return;
    setBusyNo(requestNo);
    try {
      const detail = await shippingApi.reissueShipRequest(requestNo);
      await load();
      notify();
      setPreviewNo(detail.request_no);
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || '再発行に失敗しました');
    } finally {
      setBusyNo(null);
    }
  };

  const handleCancel = async (requestNo) => {
    if (!window.confirm(`依頼No ${requestNo} を取消しますか？`)) return;
    setBusyNo(requestNo);
    try {
      await shippingApi.cancelShipRequest(requestNo);
      await load();
      notify();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || '取消に失敗しました');
    } finally {
      setBusyNo(null);
    }
  };

  const handleArrange = async (requestNo, next) => {
    setBusyNo(requestNo);
    try {
      await shippingApi.arrangeShipRequest(requestNo, next);
      await load();
      notify();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || '手配状態の更新に失敗しました');
    } finally {
      setBusyNo(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="inline-flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={latestOnly}
            onChange={(e) => setLatestOnly(e.target.checked)}
          />
          有効のみ
        </label>
        <label className="inline-flex items-center gap-1.5">
          手配:
          <select
            className="border border-slate-300 rounded px-2 py-1 text-sm"
            value={arrangeFilter}
            onChange={(e) => setArrangeFilter(e.target.value)}
          >
            <option value="">すべて</option>
            <option value="pending">未手配</option>
            <option value="arranged">手配済</option>
          </select>
        </label>
        {isStaff && (
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(e) => setIncludeCancelled(e.target.checked)}
            />
            取消を含む
          </label>
        )}
        <button
          type="button"
          className="px-2 py-1 text-xs border border-slate-300 rounded"
          onClick={load}
        >
          再読込
        </button>
      </div>

      {loading && <p className="text-sm text-slate-500">読み込み中…</p>}
      {error && <p className="text-sm text-red-600 whitespace-pre-wrap">{String(error)}</p>}

      <div className="overflow-auto border border-slate-200 rounded max-h-[70vh]">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr className="text-left text-slate-600">
              <th className="px-2 py-2 font-medium">依頼No</th>
              <th className="px-2 py-2 font-medium">発送日</th>
              <th className="px-2 py-2 font-medium">発行日</th>
              <th className="px-2 py-2 font-medium">発行者</th>
              <th className="px-2 py-2 font-medium">発注No</th>
              <th className="px-2 py-2 font-medium">送り先</th>
              <th className="px-2 py-2 font-medium">仕入先</th>
              <th className="px-2 py-2 font-medium">手配</th>
              <th className="px-2 py-2 font-medium">状態</th>
              <th className="px-2 py-2 font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={10} className="px-2 py-6 text-center text-slate-500">
                  依頼がありません
                </td>
              </tr>
            ) : (
              items.map((row) => {
                const busy = busyNo === row.request_no;
                return (
                  <tr key={row.request_no} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        className="text-blue-600 hover:underline font-medium"
                        onClick={() => setPreviewNo(row.request_no)}
                      >
                        {row.request_no}
                      </button>
                      {row.is_latest ? (
                        <span className="ml-1 text-[10px] px-1 rounded border border-emerald-600 text-emerald-800">
                          有効
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-medium text-slate-800">
                      {formatShareDate(row.shipment_date)}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {formatShareDate(row.issued_at)}
                    </td>
                    <td className="px-2 py-1.5">{row.issued_by || '-'}</td>
                    <td className="px-2 py-1.5">{row.hacchu_no}</td>
                    <td className="px-2 py-1.5 max-w-[10rem] truncate" title={row.destination_name || ''}>
                      {row.destination_name || '-'}
                    </td>
                    <td className="px-2 py-1.5 max-w-[8rem] truncate">{row.supplier_name || '-'}</td>
                    <td className="px-2 py-1.5">{arrangeLabel(row.arrange_status)}</td>
                    <td className="px-2 py-1.5">
                      {row.status === 'cancelled' ? '取消' : '発行'}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          className="text-xs px-1.5 py-0.5 border rounded"
                          onClick={() => setPreviewNo(row.request_no)}
                        >
                          印刷
                        </button>
                        {row.is_latest && row.status === 'issued' && (
                          <button
                            type="button"
                            disabled={busy}
                            className="text-xs px-1.5 py-0.5 border rounded"
                            onClick={() =>
                              handleArrange(
                                row.request_no,
                                row.arrange_status === 'arranged' ? 'pending' : 'arranged'
                              )
                            }
                          >
                            {row.arrange_status === 'arranged' ? '未手配に戻す' : '手配済'}
                          </button>
                        )}
                        {isStaff && row.is_latest && row.status === 'issued' && (
                          <>
                            <button
                              type="button"
                              disabled={busy}
                              className="text-xs px-1.5 py-0.5 border rounded"
                              onClick={() => handleReissue(row.request_no)}
                            >
                              再発行
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              className="text-xs px-1.5 py-0.5 border rounded text-red-700"
                              onClick={() => handleCancel(row.request_no)}
                            >
                              取消
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ShipRequestPreviewModal
        isOpen={!!previewNo}
        requestNo={previewNo}
        onClose={() => setPreviewNo(null)}
      />
    </div>
  );
}
